import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { isRamEmbeddedInCockpit, requestCockpitNavigation } from '@/shared/embed/ram-to-cockpit-navigation';
import { useConfig } from '@/shared/utils/config/config-context';
import { cockpitContactDetailUrl } from '@/shared/utils/cockpit-links';
import { cn } from '@/shared/utils';

export interface ContactNavLinkProps {
    contactId: number;
    className?: string;
    children: ReactNode;
}

/**
 * Contact detail link that works standalone and inside Cockpit's RAM iframe:
 * - standalone → RAM `/contacts/:id`
 * - embedded → Cockpit `contactDetail.info` href (new-tab) + parent router on plain click
 */
export function ContactNavLink({ contactId, className, children }: ContactNavLinkProps) {
    const { config } = useConfig();
    const ramTo = `/contacts/${contactId}`;

    if (!isRamEmbeddedInCockpit()) {
        return (
            <Link to={ramTo} className={className}>
                {children}
            </Link>
        );
    }

    const cockpitHref = cockpitContactDetailUrl(config?.S_COCKPIT_URL, contactId);
    if (!cockpitHref) {
        return (
            <Link to={ramTo} className={className}>
                {children}
            </Link>
        );
    }

    return (
        <a
            href={cockpitHref}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(className)}
            onClick={e => {
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
                e.preventDefault();
                requestCockpitNavigation({
                    name: 'contactDetail.info',
                    params: { contactId },
                });
            }}
        >
            {children}
        </a>
    );
}
